import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import DefaultApiLimit from '../../../../config/defaultLimit';
import {
  ConsumerTransactionHistoryInterFace,
  RequestConsumerTransactionHistoryInterFace,
} from '../../../../interface/end-consumer-transaction-history-interface';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import {
  fetchConsumerTransactionHistory,
  fetchConsumerTransactionHistoryFilter,
  setSortData,
  updateConsumerTransactionHistoryListFilter,
} from '../../../../redux/slices/EndConsumerTransactionHistory';
import { setNotificationMessage } from '../../../../redux/slices/NotificationManagementSlice';
import CustomTable from '../../../../shared-components/custom-table/CustomTable';
// import PaymentDetails from '../../../../shared-components/table-side-panel/PaymentDetails';
import EmspCdrSidePanel from '../../../../shared-components/table-side-panel/EmspCdrSidePanel';
import EmspSessionSidePanel from '../../../../shared-components/table-side-panel/EmspSessionSidePanel';
import EndConsumerSidePanel from '../../../../shared-components/table-side-panel/EndConsumerSidePanel';
import SidePaneModel from '../../../../shared-components/table-side-panel/SidePaneModel';
import { GetParametersFromCommonFilters } from '../../../../shared-functions/CommonFilters';
import ConvertUTCtoDate from '../../../../shared-functions/ConvertUTCtoDate';
import ConvertUTCtoDateTime from '../../../../shared-functions/ConvertUTCtoDateTime';
import { convertUtcTo12HourTime } from '../../../../shared-functions/DateTime';
import { exportCSV, exportPDF } from '../../../../shared-functions/ExportFile';
import ToFixedValue from '../../../../shared-functions/ToFixedValue';
import endConsumerApi from '../../../../utils/api/end-consumer-v2';

interface ModelDetailsInterface {
  title?: string;
  content: JSX.Element;
  url?: string;
}

/**
 * Charge Session List
 * @returns {any} - The TransactionHistoryList component
 */
function TransactionHistoryList() {
  const { t, i18n } = useTranslation('endConsumer');
  const dispatch = useAppDispatch();
  const [hiddenColumns, setHiddenColumns] = useState<string[]>([]);
  const [exportLoader, setExportLoader] = useState<boolean>(false);
  const navigate = useNavigate();
  const {
    consumers,
    limit,
    page,
    totalPages,
    totalResults,
    loading,
    filterListLoading,
    filters,
    isFilterUpdated,
    sortField,
    sortBy,
  } = useAppSelector((state) => state.ConsumerTransactionHistory);
  const { selectedEndConsumer } = useAppSelector((state) => state.endConsumerType);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [modelDetails, setModelDetails] = useState<ModelDetailsInterface>({
    title: '',
    content: <div />,
    url: '',
  });

  /**
   * Handle open station details
   * @param {any} rowData - The rowData
   */
  const handleOpenTransactionDetails = (rowData: ConsumerTransactionHistoryInterFace) => {
    navigate(`?cdrObjectId=${rowData?._id}`);
    setIsOpen(true);
    setModelDetails({
      title: t('transactionDetails'),
      content: <EmspCdrSidePanel fetchIdFrom="queryParams" />,
    });
  };

  /**
   * Function to close modal
   */
  const handleCloseModal = () => {
    setIsOpen(false);
    setModelDetails({ title: '', content: <div /> });
  };

  /**
   * Fetch Users list function
   * @param {any} data - The data
   */
  const fetchData = (data: RequestConsumerTransactionHistoryInterFace) => {
    if (!loading && selectedEndConsumer) {
      dispatch(
        fetchConsumerTransactionHistory({
          userCustomId: selectedEndConsumer.userCustomId,
          ...data,
        }),
      );
    }
  };
  const column = [
    {
      label: t('date'),
      accessorKey: 'createdAt' as keyof ConsumerTransactionHistoryInterFace,
      sortAllow: true,
      /**
       * Custom data function
       * @param {any} currentRow - current row data
       * @returns {any} - custom data
       */
      customData: (currentRow: ConsumerTransactionHistoryInterFace) => (
        <span>{ConvertUTCtoDateTime(currentRow?.createdAt)}</span>
      ),
    },
    {
      label: t('transactionId'),
      accessorKey: 'chargingTransactionId' as keyof ConsumerTransactionHistoryInterFace,
      sortAllow: false,
      /**
       * Rendering device name with hyper link
       * @param {any} currentRow - Current row data
       * @returns {any} - Device name cell
       */
      customData: (currentRow: ConsumerTransactionHistoryInterFace) => (
        <p
          role="presentation"
          className="hyper-link"
          onClick={() => handleOpenTransactionDetails(currentRow)}>
          {currentRow?.chargingTransactionId || ''}
        </p>
      ),
    },
    {
      label: t('locationId'),
      accessorKey: 'locationId' as keyof ConsumerTransactionHistoryInterFace,
      /**
       * Custom rendering of Location ID column.
       * @param {ConsumerTransactionHistoryInterFace} rowData - The row data.
       * @returns  The rendered location ID.
       */
      customData: (rowData: ConsumerTransactionHistoryInterFace) => {
        const locationId = rowData.location?.id || rowData.locationId || t('na');
        return <span>{locationId}</span>;
      },
    },
    {
      label: t('driverId'),
      accessorKey: 'userCustomId' as keyof ConsumerTransactionHistoryInterFace,
      queryParamsKey: 'userCustomId',
      queryParamsValue: 'userCustomId',
      sortAllow: true,
      sidePanelComponent: <EndConsumerSidePanel fetchIdFrom="queryParams" />,
    },
    {
      label: t('paymentMethod'),
      accessorKey: 'paymentMethod' as keyof ConsumerTransactionHistoryInterFace,
      sortAllow: false,
      /**
       * Rendering payment method with translation
       * @param {any} currentRow - current row data
       * @returns {any} - custom data
       */
      customData: (currentRow: ConsumerTransactionHistoryInterFace) => (
        <p>{currentRow?.paymentMethod ? t(currentRow.paymentMethod) : t('na')}</p>
      ),
    },
    {
      label: t('partyId', { defaultValue: 'Party ID' }),
      accessorKey: 'partyId' as any,
      sortAllow: true,
      /**
       * Render Party ID.
       * @param {ConsumerTransactionHistoryInterFace} rowData - The row data.
       * @returns {React.JSX.Element} The Party ID element.
       */
      customData: (rowData: ConsumerTransactionHistoryInterFace) => (
        <span>{rowData.partyId || (rowData as any).cdrDetails?.partyId || t('na')}</span>
      ),
    },
    {
      label: t('sessionId', { defaultValue: 'Session ID' }),
      accessorKey: 'sessionId' as any,
      queryParamsKey: 'sessionId',
      queryParamsValue: 'sessionId',
      sidePanelComponent: <EmspSessionSidePanel fetchIdFrom="queryParams" />,
      sortAllow: true,
    },
    {
      label: t('energy'),
      accessorKey: 'totalEnergy' as keyof ConsumerTransactionHistoryInterFace,
      sortAllow: true,
      /**
       * Render total energy in kWh.
       * @param {ConsumerTransactionHistoryInterFace} rowData - The row data.
       * @returns  The total energy element.
       */
      customData: (rowData: ConsumerTransactionHistoryInterFace) => (
        <span>
          {rowData.totalEnergy !== undefined && rowData.totalEnergy !== null
            ? `${ToFixedValue(rowData.totalEnergy, 3)} kWh`
            : `0.00 kWh`}
        </span>
      ),
    },
    {
      label: t('CDRamount'),
      accessorKey: 'totalCost' as keyof ConsumerTransactionHistoryInterFace,
      sortAllow: true,
      /**
       * Render total cost with currency.
       * @param {ConsumerTransactionHistoryInterFace} rowData - The row data.
       * @returns  The total cost element.
       */
      customData: (rowData: ConsumerTransactionHistoryInterFace) => (
        <span>
          {rowData.totalCost !== undefined && rowData.totalCost !== null
            ? `${ToFixedValue(rowData.totalCost)} ${rowData.currency || 'SAR'}`
            : `0.00 ${rowData.currency || 'SAR'}`}
        </span>
      ),
    },
    {
      label: t('mobilityAmount'),
      accessorKey: 'platform' as keyof ConsumerTransactionHistoryInterFace,
      /**
       * Render total cost with currency.
       * @param {ConsumerTransactionHistoryInterFace} rowData - The row data.
       * @returns  The total cost element.
       */
      customData: (rowData: ConsumerTransactionHistoryInterFace) => (
        // <span>
        //   {rowData.sessionAmount || rowData.platformAmount
        //     ? `${ToFixedValue(Number(rowData.sessionAmount) + Number(rowData.platformAmount))} ${rowData.currency || 'SAR'}`
        //     : `0.00 ${rowData.currency || 'SAR'}`}
        // </span>
        <span>
          {rowData.platformAmount
            ? `${ToFixedValue(Number(rowData.platformAmount))} ${rowData.currency || 'SAR'}`
            : `0.00 ${rowData.currency || 'SAR'}`}
        </span>
      ),
    },
    {
      label: t('sessionAmount'),
      accessorKey: 'sessionAmount' as keyof ConsumerTransactionHistoryInterFace,
      sortAllow: true,
      /**
       * Render session amount with currency.
       * @param {ConsumerTransactionHistoryInterFace} rowData - The row data.
       * @returns The session amount element.
       */
      customData: (rowData: ConsumerTransactionHistoryInterFace) => (
        <span>
          {rowData.sessionAmount !== undefined && rowData.sessionAmount !== null
            ? `${ToFixedValue(Number(rowData.sessionAmount))} ${rowData.currency || 'SAR'}`
            : `0.00 ${rowData.currency || 'SAR'}`}
        </span>
      ),
    },
    {
      label: t('invoiceId', { defaultValue: 'Invoice ID' }),
      accessorKey: 'invoiceNumber' as any,
      sortAllow: true,
      /**
       * Render Invoice ID.
       * @param {ConsumerTransactionHistoryInterFace} rowData - The row data.
       * @returns {React.JSX.Element} The Invoice ID element.
       */
      customData: (rowData: ConsumerTransactionHistoryInterFace) => (
        <span>{rowData.invoiceNumber || (rowData as any).invoiceId || t('na')}</span>
      ),
    },
    {
      label: t('finalAmount'),
      accessorKey: 'finalAmount' as keyof ConsumerTransactionHistoryInterFace,
      sortAllow: true,
      /**
       * Render total cost with currency.
       * @param {ConsumerTransactionHistoryInterFace} rowData - The row data.
       * @returns  The total cost element.
       */
      customData: (rowData: ConsumerTransactionHistoryInterFace) => (
        <span>
          {rowData.finalAmount !== undefined && rowData.finalAmount !== null
            ? `${ToFixedValue(rowData.finalAmount)} ${rowData.currency || 'SAR'}`
            : `0.00 ${rowData.currency || 'SAR'}`}
        </span>
      ),
    },
    {
      label: t('status'),
      accessorKey: 'status' as keyof ConsumerTransactionHistoryInterFace,
      sortAllow: false,
      /**
       * status
       * @param {any} currentRow - The currentRow
       */
      customData: (currentRow: ConsumerTransactionHistoryInterFace) => {
        if (currentRow?.status === 'captured') {
          return <p className="success">Success</p>;
        }
        return (
          <p className={`${currentRow?.status}`}>
            {currentRow?.status
              ? currentRow.status.charAt(0).toUpperCase() + currentRow.status.slice(1).toLowerCase()
              : 'NA'}{' '}
          </p>
        );
      },
    },
  ];

  /**
   * Current columns for Transaction History export
   */
  const currentColumns = [
    { label: t('sNo'), accessorKey: '__serial__' },
    { label: t('date'), accessorKey: 'createdAt' },
    { label: t('transactionId'), accessorKey: 'chargingTransactionId' },
    { label: t('locationId'), accessorKey: 'locationId' },
    { label: t('driverId'), accessorKey: 'userCustomId' },
    { label: t('paymentMethod'), accessorKey: 'paymentMethod' },
    { label: t('partyId'), accessorKey: 'partyId' },
    { label: t('sessionId'), accessorKey: 'sessionId' },
    { label: t('energy'), accessorKey: 'totalEnergy' },
    { label: t('CDRamount'), accessorKey: 'totalCost' },
    { label: t('mobilityAmount'), accessorKey: 'platform' },
    { label: t('invoiceId'), accessorKey: 'invoiceNumber' },
    { label: t('finalAmount'), accessorKey: 'finalAmount' },
    { label: t('status'), accessorKey: 'status' },
  ];

  /**
   * Filtering columns for export without hidden columns
   */
  const exportCurrentColumns = currentColumns.filter(
    (col) => col.accessorKey === '__serial__' || !hiddenColumns.includes(col.accessorKey),
  );

  /**
   * Export data function
   * @param {any} type - The type
   */
  const exportDataPdfOrCSV = (type: string) => {
    setExportLoader(true);

    const queryParameters = GetParametersFromCommonFilters(filters);
    const payload: any = { ...queryParameters };

    if (sortField) payload.sortField = sortField;
    if (sortBy) payload.sortBy = sortBy;

    if (!selectedEndConsumer?.userCustomId) return;

    endConsumerApi
      .fetchCommercialTransactionExport({ userCustomId: selectedEndConsumer.userCustomId, payload })
      .then((response) => {
        /**
         * Dynamic Headers
         */
        const headers = exportCurrentColumns.map((col) => col.label);

        /**
         * Dynamic Rows
         */
        const rows = response.data.map((row: ConsumerTransactionHistoryInterFace, index: number) =>
          exportCurrentColumns.map((col) => {
            switch (col.accessorKey) {
              case '__serial__':
                return index + 1;

              case 'createdAt':
                return row?.createdAt
                  ? `${ConvertUTCtoDate(row.createdAt)} ${convertUtcTo12HourTime(row.createdAt)}`
                  : t('na');

              case 'chargingTransactionId':
                return row?.externalId || t('na');

              case 'locationId':
                return row?.location?.id || t('na');

              case 'userCustomId':
                return row?.userCustomId || t('na');

              case 'paymentMethod':
                return row?.paymentMethod || t('na');

              case 'partyId':
                return row?.partyId || t('na');

        
              case 'sessionId':
                return row?.sessionId || t('na');

              case 'totalEnergy':
                return ToFixedValue(row?.totalEnergy, 3) || t('na');

              case 'totalCost':
                return `${ToFixedValue(row?.totalCost) || t('na')} ${row.currency || 'SAR'}`;

              case 'platform':
                return `${ToFixedValue(row?.platformAmount) || t('na')} ${row.currency || 'SAR'}`;

              case 'finalAmount':
                return `${ToFixedValue(row?.finalAmount) || t('na')} ${row.currency || 'SAR'}`;

              case 'status':
                return row?.status || t('na');

              default:
                return (row as any)[col.accessorKey] || t('na');
            }
          }),
        );

        if (rows.length > 0) {
          if (type === 'csv') {
            exportCSV(headers, rows, 'end-consumer-transaction-history.csv');
          } else {
            exportPDF(headers, rows, 'end-consumer-transaction-history.pdf');
          }
        }

        setExportLoader(false);
      })
      .catch((error) => {
        setExportLoader(false);
        dispatch(
          setNotificationMessage({
            message: error?.response?.data?.message,
            status: true,
            type: 'error',
            code: error?.response?.data?.code,
          }),
        );
      });
  };

  /**
   * Table pagination function
   * @param {any} value - Pagination limit value
   */
  const pageNateLimitChange = (value: any) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({ ...queryParameters, ...value, sortField, sortBy });
  };

  /**
   * Handle the filter data clear function
   * @param {any} sortData - The sortData
   */
  const handleSortFunction = (sortData: any) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    dispatch(setSortData(sortData));
    fetchData({
      ...queryParameters,
      sortBy: sortData.sortBy,
      sortField: sortData.sortField,
      page: DefaultApiLimit.page,
      limit,
    });
  };

  /**
   * Function to refresh data
   */
  const handleRefresh = () => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({ ...queryParameters, page, limit, sortField, sortBy });
  };

  useEffect(() => {
    if (!selectedEndConsumer?.userCustomId) return;

    const currentId = selectedEndConsumer.userCustomId;
    const lastId = sessionStorage.getItem('TransactionHistoryV2');

    if (isFilterUpdated || consumers.length === 0 || lastId !== currentId) {
      if (selectedEndConsumer?.userCustomId) {
        const queryParameters = GetParametersFromCommonFilters(filters);
        fetchData({
          page: DefaultApiLimit.page,
          limit,
          ...queryParameters,
        });
      }
    }
    sessionStorage.setItem('TransactionHistoryV2', currentId);
  }, [filters, selectedEndConsumer]);

  return (
    <div className="charging-session-wrap" dir={i18n.dir()}>
      <SidePaneModel
        open={isOpen}
        title={modelDetails.title}
        content={modelDetails.content}
        closePanel={handleCloseModal}
        url={modelDetails.url}
      />{' '}
      <CustomTable
        dataKey="EndConsumerManagement"
        header={column}
        data={consumers}
        serialNo
        actionButton
        sizeChangeButtonRequired
        version="v2"
        height={500}
        page={page}
        limit={limit}
        loading={loading}
        totalPages={totalPages}
        totalResults={totalResults}
        sortData={{ sortBy, sortField }}
        handleSortChange={handleSortFunction}
        pageLimitChange={pageNateLimitChange}
        filters={filters}
        filtersToDispatch={updateConsumerTransactionHistoryListFilter}
        filterListLoading={filterListLoading}
        filterListToDispatch={fetchConsumerTransactionHistoryFilter}
        exportData={consumers}
        exportLoader={exportLoader}
        exportDataPdfOrCSV={exportDataPdfOrCSV}
        refreshRequired
        requiredRefreshToggle
        refershCurrentPages={handleRefresh}
        refreshFunction={handleRefresh}
        refreshInterval={10}
        hoverLabel={t('refresh')}
        leftTopContent={<> </>}
        expandTableOpen
        expandTableOpenKey="TransactionHistoryV2ListEndConsumer"
        onHiddenColumnsChange={setHiddenColumns}
      />
    </div>
  );
}

export default TransactionHistoryList;
